import { ptBR } from '@/i18n/pt-BR';

/**
 * Vocabulário da trilha → frase da tela. // ux-neutrality:allow doc-comment
 *
 * O servidor devolve o código do fato; quem o traduz é o catálogo. A cobertura // ux-neutrality:allow doc-comment
 * do catálogo NÃO é confiada à revisão: `types/auditoria.ACOES_TRILHA` declara // ux-neutrality:allow doc-comment
 * tudo o que os seis ramos da trilha podem emitir, e // ux-neutrality:allow doc-comment
 * `tests/unit/test_e61_f4_paridade_catalogo_trilha.py` reprova ação ou origem // ux-neutrality:allow doc-comment
 * sem chave aqui — e chave órfã aqui também. // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * O default existe só porque a resposta chega como texto. Ele diz que o fato // ux-neutrality:allow doc-comment
 * foi registrado, e não o código interno dele: devolver o código era o que // ux-neutrality:allow doc-comment
 * publicava `escritorio_criado` para o gestor (F-E61.F4-05). A linha não perde // ux-neutrality:allow doc-comment
 * valor de trilha — quando, quem e sobre o quê continuam na própria linha. // ux-neutrality:allow doc-comment
 *
 * Mora fora de `components/Admin/` porque as DUAS telas da trilha usam o mesmo // ux-neutrality:allow doc-comment
 * catálogo: o console da plataforma (E53.F7) e o módulo do banco (E61.F4). O // ux-neutrality:allow doc-comment
 * rótulo de AMBIENTE ficou no console (`components/Admin/ambiente_labels.ts`) — // ux-neutrality:allow doc-comment
 * é vocabulário de console, usado por outras três telas de lá, e a tela do // ux-neutrality:allow doc-comment
 * banco não mostra essa coluna. // ux-neutrality:allow doc-comment
 */
const _ORIGENS: Record<string, string> = {
  administracao: ptBR.trilha.origemAdministracao,
  assinatura: ptBR.trilha.origemAssinatura,
  medicao: ptBR.trilha.origemMedicao,
  requisicao: ptBR.trilha.origemRequisicao,
  edicao_laudo: ptBR.trilha.origemEdicaoLaudo,
  compartilhamento_laudo: ptBR.trilha.origemCompartilhamento,
  caso: ptBR.trilha.origemCaso,
};

const _ACOES: Record<string, string> = {
  cliente_criado: ptBR.trilha.acaoClienteCriado,
  // E61.F4.C06: o banco abre escritório (`banco_escritorios_service`) e o fato
  // ficou de fora do catálogo — o gestor viu o código na tela (F-E61.F4-05).
  escritorio_criado: ptBR.trilha.acaoEscritorioCriado,
  escritorio_vinculado: ptBR.trilha.acaoEscritorioVinculado,
  escritorio_desvinculado: ptBR.trilha.acaoEscritorioDesvinculado,
  escritorio_marcado_plataforma: ptBR.trilha.acaoEscritorioMarcadoPlataforma,
  escritorio_desmarcado_plataforma: ptBR.trilha.acaoEscritorioDesmarcadoPlataforma,
  usuario_criado: ptBR.trilha.acaoUsuarioCriado,
  papel_alterado: ptBR.trilha.acaoPapelAlterado,
  acesso_desativado: ptBR.trilha.acaoAcessoDesativado,
  acesso_reativado: ptBR.trilha.acaoAcessoReativado,
  laudo_assinado: ptBR.trilha.acaoLaudoAssinado,
  exame_entregue: ptBR.trilha.acaoExameEntregue,
  certificacao_requerida: ptBR.trilha.acaoCertificacaoRequerida,
  narrativa_editada: ptBR.trilha.acaoNarrativaEditada,
  lr_ajustado: ptBR.trilha.acaoLrAjustado,
  laudo_compartilhado: ptBR.trilha.acaoLaudoCompartilhado,
  // E61.F3: a ação nasceu com a trilha do banco e é a mais relevante para ele —
  // sem entrada aqui, a tela mostraria o código cru (E61.F4.T02).
  laudo_certificado_baixado: ptBR.trilha.acaoLaudoCertificadoBaixado,
  // E65.F3 (ADR-0069): único evento que o PRÓPRIO usuário grava sobre si. O
  // rótulo diz quem agiu porque a trilha também registra ações que terceiros
  // fazem sobre a conta — sem isso, "senha alterada" seria ambíguo justamente
  // onde a distinção importa.
  senha_trocada: ptBR.trilha.acaoSenhaTrocada,
  // E65.F6: a saída do arquivo ORIGINAL do caso — o que carrega a PII forense.
  // Vale para os dois ambientes, e é o banco quem lê os próprios eventos.
  documento_baixado: ptBR.trilha.acaoDocumentoBaixado,
  // E69.F1 (ADR-0072): o primeiro par que fala do ambiente inteiro — encerrar
  // desativa todo mundo no mesmo passo; reabrir não reativa ninguém.
  escritorio_encerrado: ptBR.trilha.acaoEscritorioEncerrado,
  escritorio_reaberto: ptBR.trilha.acaoEscritorioReaberto,
  // E175.F1 (ADR-0127): o painel da pessoa no console.
  pessoa_editada: ptBR.trilha.acaoPessoaEditada,
  senha_provisoria_definida: ptBR.trilha.acaoSenhaProvisoriaDefinida,
  pessoa_movida: ptBR.trilha.acaoPessoaMovida,
  pessoa_excluida: ptBR.trilha.acaoPessoaExcluida,
  // E137.F3 (ADR-0093): os fatos da trilha do caso, com o mesmo rótulo do histórico.
  recebimento_selado: ptBR.historicoDoCaso.tipo.recebimento_selado,
  conferencia_registrada: ptBR.historicoDoCaso.tipo.conferencia_registrada,
  ocorrencia_aberta: ptBR.historicoDoCaso.tipo.ocorrencia_aberta,
  ocorrencia_resolvida: ptBR.historicoDoCaso.tipo.ocorrencia_resolvida,
  exame_verificado: ptBR.historicoDoCaso.tipo.exame_verificado,
  evento_corrigido: ptBR.historicoDoCaso.tipo.evento_corrigido,
  laudo_editado: ptBR.historicoDoCaso.tipo.laudo_editado,
  narrativa_gerada: ptBR.historicoDoCaso.tipo.narrativa_gerada,
  narrativa_aprovada: ptBR.historicoDoCaso.tipo.narrativa_aprovada,
  narrativa_reaberta: ptBR.historicoDoCaso.tipo.narrativa_reaberta,
  pre_analise_executada: ptBR.historicoDoCaso.tipo.pre_analise_executada,
  documento_emitido: ptBR.historicoDoCaso.tipo.documento_emitido,
  // E138.F2 (ADR-0094): a conferência do arquivo apresentado e o download recusado por divergência.
  documento_conferido: ptBR.historicoDoCaso.tipo.documento_conferido,
  documento_divergente: ptBR.historicoDoCaso.tipo.documento_divergente,
  // E139.F1 (ADR-0095): a ficha da leitura gravada ao fim do lote do exame.
  ficha_da_leitura_gravada: ptBR.historicoDoCaso.tipo.ficha_da_leitura_gravada,
  // E140.F1 (ADR-0096): a conferência da leitura contra o inventário, gravada com cada ficha.
  leitura_conferida: ptBR.historicoDoCaso.tipo.leitura_conferida,
  // E140.F2 (ADR-0096 §2.6): a retomada da leitura e a limitação registrada pela equipe.
  leitura_retomada: ptBR.historicoDoCaso.tipo.leitura_retomada,
  limitacao_da_leitura_registrada: ptBR.historicoDoCaso.tipo.limitacao_da_leitura_registrada,
  // E141.F1 (ADR-0079 E.26): o recorte ou o giro de uma fotografia ajustado pelo perito.
  fotografia_recortada: ptBR.historicoDoCaso.tipo.fotografia_recortada,
  // E144.F3 (ADR-0098 §2.6): o prazo, a urgência ou a observação do pedido do cliente.
  pedido_atualizado: ptBR.historicoDoCaso.tipo.pedido_atualizado,
  // E145.F2 (ADR-0099 §2.6): o link do QR da versão emitida, gerado e cancelado pela equipe.
  acesso_por_qr_emitido: ptBR.historicoDoCaso.tipo.acesso_por_qr_emitido,
  acesso_por_qr_revogado: ptBR.historicoDoCaso.tipo.acesso_por_qr_revogado,
  // E146.F1 (ADR-0100 §2.6): o arquivo que o envio avulso não recebeu, sem o arquivo nem o nome.
  arquivo_recusado: ptBR.historicoDoCaso.tipo.arquivo_recusado,
  // E147.F1 (ADR-0101): a conferência de uma etapa do exame, gravada quando muda.
  etapa_conferida: ptBR.historicoDoCaso.tipo.etapa_conferida,
  // E147.F2 (ADR-0101 §2.5–§2.6): o pedido da equipe sobre a consulta e a assinatura repetida.
  etapa_retomada: ptBR.historicoDoCaso.tipo.etapa_retomada,
  limitacao_da_etapa_registrada: ptBR.historicoDoCaso.tipo.limitacao_da_etapa_registrada,
  assinatura_reconciliada: ptBR.historicoDoCaso.tipo.assinatura_reconciliada,
  // E149.F1 (ADR-0102 §3.5): a mensagem da conversa do caso, sem o texto.
  mensagem_do_caso: ptBR.historicoDoCaso.tipo.mensagem_do_caso,
  // E155.F1 (ADR-0105 §2.5): a informação revista na fonte, sem o valor nem o motivo.
  leitura_revista: ptBR.historicoDoCaso.tipo.leitura_revista,
  // E165.F1.T06 (ADR-0114 §2): o envio vinculado a este caso por escolha de quem enviou.
  envio_vinculado: ptBR.historicoDoCaso.tipo.envio_vinculado,
  // E170.F1.C02, E177.F1 e E177.F3: os tipos que a trilha passou a emitir sem rótulo aqui — a tela
  // mostraria o código (a paridade da E61.F4 reprovava).
  certificacao_revogada: ptBR.historicoDoCaso.tipo.certificacao_revogada,
  origem_informada: ptBR.historicoDoCaso.tipo.origem_informada,
  ccb_divergente_mantida: ptBR.historicoDoCaso.tipo.ccb_divergente_mantida,
  // E184.F4 (GUS-145 item 5): a análise morfológica refeita sobre o par eleito.
  analise_morfologica_refeita: ptBR.historicoDoCaso.tipo.analise_morfologica_refeita,
  // E203.F4 (ADR-0146 §6–§9): o dado revisto pelo perito, pelo painel do documento.
  dado_revisto: ptBR.historicoDoCaso.tipo.dado_revisto,
  // E214.F1 (GUS-314): o recorte manual na aba Documentos.
  documento_recortado: ptBR.historicoDoCaso.tipo.documento_recortado,
  // E216.F4 (GUS-317): o arquivo excluído da análise na aba Documentos.
  documento_excluido: ptBR.historicoDoCaso.tipo.documento_excluido,
  // E224.F2 (ADR-0151 §5): a equipe cancelou o caso, com motivo.
  caso_cancelado: ptBR.historicoDoCaso.tipo.caso_cancelado,
};

export function origemLabel(fonte: string): string {
  return _ORIGENS[fonte] ?? ptBR.trilha.origemDesconhecida;
}

export function acaoLabel(acao: string): string {
  return _ACOES[acao] ?? ptBR.trilha.acaoDesconhecida;
}
